import { Suspense, lazy, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
const ScrollToTop = lazy(() => import("@/components/ScrollToTop"));
import LoadingScreen from "@/components/LoadingScreen";
import MaintenanceGuard from "@/components/MaintenanceGuard";
const AgeGate = lazy(() => import("@/components/AgeGate"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import PageTracker from "@/components/PageTracker";

// Leniwe ładowanie głównych stron
const Index = lazy(() => import("./pages/Index"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const RegulaminPage = lazy(() => import("./pages/RegulaminPage"));
const SuppliersPage = lazy(() => import("./pages/SuppliersPage"));
const SupplierVigilionePage = lazy(() => import("./pages/SupplierVigilionePage"));
const SupplierFiorePage = lazy(() => import("./pages/SupplierFiorePage"));
const SupplierGalantinoPage = lazy(() => import("./pages/SupplierGalantinoPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentFailedPage = lazy(() => import("./pages/PaymentFailedPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));

import { ThemeProvider } from "next-themes";

const queryClient = new QueryClient();

const App = () => {
    const [showAgeGate, setShowAgeGate] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);

    useEffect(() => {
        const ageConfirmed = localStorage.getItem('ageConfirmed');
        if (ageConfirmed === 'true') {
            setShowAgeGate(false);
        }
    }, []);

    // Global scroll lock for modals
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const overlays = document.querySelectorAll(
                '.fixed.inset-0.z-40, .fixed.inset-0.z-50, .fixed.inset-0.z-\\[60\\], .fixed.inset-0.z-\\[100\\], .fixed.inset-0.z-\\[110\\], .fixed.inset-0.z-\\[9999\\]'
            );
            
            let hasVisibleModal = false;
            overlays.forEach(el => {
                if (!el.classList.contains('pointer-events-none') && !el.classList.contains('hidden') && !el.classList.contains('opacity-0')) {
                    hasVisibleModal = true;
                }
            });

            // Also check for dialog elements if any
            if (document.querySelector('dialog[open]')) {
                hasVisibleModal = true;
            }

            if (hasVisibleModal) {
                document.body.style.overflow = 'hidden';
                document.documentElement.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
                document.documentElement.style.overflow = '';
            }
        });

        observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'open'] });
        return () => observer.disconnect();
    }, []);

    const handleAgeConfirm = () => {
        localStorage.setItem('ageConfirmed', 'true');
        setShowAgeGate(false);
    };

    const handleAgeDeny = () => {
        setAccessDenied(true);
    };

    return (
    <HelmetProvider>
        <QueryClientProvider client={queryClient}>
            <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
                <LanguageProvider>
                    <CartProvider>
                        <TooltipProvider>
                            <Toaster />
                            <Sonner />

                            <BrowserRouter>
                                <PageTracker />
                            <Suspense fallback={<LoadingScreen />}>
                            <Suspense fallback={null}>
                                <ScrollToTop />
                                <CookieConsent />
                                <AgeGate
                                    isOpen={showAgeGate}
                                    onConfirm={handleAgeConfirm}
                                    onDeny={handleAgeDeny}
                                />
                            </Suspense>
                                {accessDenied && (
                                    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4 text-center">
                                        <div>
                                            <h1 className="mb-4 font-display text-3xl font-bold text-white">
                                                Dostęp zabroniony
                                            </h1>
                                            <p className="text-white/70">
                                                Strona jest przeznaczona wyłącznie dla osób pełnoletnich.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                <Routes>
                                    <Route path="/admin" element={<AdminPage />} />
                                    <Route element={<MaintenanceGuard />}>
                                        <Route path="/" element={<Index />} />
                                        <Route path="/produkty" element={<ProductsPage />} />
                                        
                                        {/* SEO: Nowy routing dla produktów */}
                                        <Route path="/produkty/:categorySlug/:id/:productSlug" element={<ProductDetailPage />} />
                                        {/* Kompatybilność wsteczna dla starych linków */}
                                        <Route path="/product/:id" element={<ProductDetailPage />} />
                                        
                                        <Route path="/checkout" element={<CheckoutPage />} />
                                        <Route path="/order-success" element={<PaymentSuccessPage />} />
                                        <Route path="/order-failed" element={<PaymentFailedPage />} />
                                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                                        <Route path="/regulamin" element={<RegulaminPage />} />
                                        <Route path="/dostawcy" element={<SuppliersPage />} />
                                        <Route path="/dostawcy/tenuta-vigilione" element={<SupplierVigilionePage />} />
                                        <Route path="/dostawcy/fiore-di-puglia" element={<SupplierFiorePage />} />
                                        <Route path="/dostawcy/galantino" element={<SupplierGalantinoPage />} />
                                        
                                        <Route path="/blog" element={<BlogPage />} />
                                        <Route path="/blog/:slug" element={<BlogPostPage />} />
                                        
                                        {/* SEO: Dynamiczny routing dla wszystkich obecnych i przyszłych kategorii produktowych */}
                                        <Route path="/produkty/:categorySlug" element={<ProductsPage />} />
                                        
                                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                                        <Route path="*" element={<NotFound />} />
                                    </Route>
                                </Routes>
                            </Suspense>
                        </BrowserRouter>
                    </TooltipProvider>
                </CartProvider>
            </LanguageProvider>
            </ThemeProvider>
        </QueryClientProvider>
    </HelmetProvider>
    );
};

export default App;
